import { useMemo, type CSSProperties, type ReactNode, type SVGProps } from 'react';

/**
 * Single-line SVG text label for charts — a drop-in replacement for `@visx/text`'s `<Text>`
 * that emits the same DOM without measuring anything.
 *
 * `<Text>` calls visx's `getStringWidth` from a `useMemo` on every render, once per word plus
 * once for a non-breaking space. That helper builds a hidden `<svg><text>` in `document.body`
 * and reads `getComputedTextLength()`, which costs two things we don't want:
 *
 * - During SSR/prerender there is no `document`, so it throws, gets swallowed by visx's own
 *   `try/catch`, and logs `ReferenceError: document is not defined` from every prerender
 *   worker. Harmless but it buries real build errors.
 * - In the browser, `getComputedTextLength()` forces synchronous layout per word on every
 *   render — real INP cost on charts that re-render on hover and live price updates.
 *
 * And the measurement is discarded either way: visx only consumes the widths when `width` or
 * `scaleToFit` is set (to wrap or shrink text), which no chart here uses. So the measurement
 * is dropped without changing a single rendered pixel.
 *
 * Multiline (`width`) and `scaleToFit` are deliberately NOT supported — both genuinely need
 * measured text. A label that needs them belongs on `@visx/text`'s `<Text>`, client-side only.
 */

const SVG_STYLE: CSSProperties = { overflow: 'visible' };

/**
 * The `dy` visx computes for the first line of a single-line label, per `verticalAnchor`.
 * Inlined from its `reduce-css-calc` expressions (`capHeight` defaults to d3's `0.71em`):
 * `start` → `calc(0.71em)`, `middle` → `calc(0 * -1em + (0.71em / 2))`, `end` → `calc(0 * -1em)`.
 */
const START_DY_BY_ANCHOR = {
    start: '0.71em',
    middle: '0.355em',
    end: '0em',
} as const;

/**
 * visx's word split: collapses whitespace runs, except a run that *begins* with a
 * non-breaking space, which is left as-is. A U+00A0 in the middle of a run still collapses,
 * since the lookahead only guards the start and U+00A0 is itself matched by `\s`.
 */
const WORD_SEPARATOR = /(?:(?!\u00A0+)\s+)/;

/** Mirrors visx's `isXOrYInValid` — a finite number, or any string (percentages). */
const isValidCoordinate = (value: number | string | undefined): boolean =>
    (typeof value === 'number' && Number.isFinite(value)) || typeof value === 'string';

export interface SvgTextProps extends Omit<SVGProps<SVGTextElement>, 'ref' | 'dx' | 'dy' | 'width'> {
    /** Horizontal offset applied to the wrapping `<svg>`, as in `@visx/text`. */
    dx?: number | string;
    /** Vertical offset applied to the wrapping `<svg>`, as in `@visx/text`. */
    dy?: number | string;
    /** Rotation in degrees about (`x`, `y`), as in `@visx/text`. Not an SVG attribute. */
    angle?: number;
    /** Vertical alignment of the line relative to `y`. Defaults to `end`, matching visx. */
    verticalAnchor?: keyof typeof START_DY_BY_ANCHOR;
    children?: ReactNode;
}

export default function SvgText({
    dx = 0,
    dy = 0,
    textAnchor = 'start',
    verticalAnchor = 'end',
    angle,
    children,
    ...textProps
}: SvgTextProps) {
    const { x = 0, y = 0, fontSize } = textProps;

    const line = useMemo(
        () => (children == null ? '' : children.toString().split(WORD_SEPARATOR).join(' ')),
        [children],
    );

    // visx keeps the wrapping `<svg>` and drops only the `<text>` when a coordinate is
    // unusable, so a NaN scale value collapses the label instead of the whole subtree.
    const hasValidPosition = isValidCoordinate(x) && isValidCoordinate(y);

    // `angle` is a visx prop, not an SVG attribute: it rotates the label about its anchor
    // point. Rotated date axes (`CompleteStoryChart`, `JuneCrisisChart`) depend on it, and
    // spreading it onto `<text>` would silently render them horizontal. Falsy `angle` yields
    // no transform, as in visx, and a caller-supplied `transform` still wins — hence spread
    // after ours, matching the order in visx's `<Text>`.
    const rotation = angle ? `rotate(${angle}, ${x}, ${y})` : undefined;

    return (
        <svg x={dx} y={dy} fontSize={fontSize} style={SVG_STYLE}>
            {hasValidPosition ? (
                <text transform={rotation} {...textProps} textAnchor={textAnchor}>
                    <tspan x={x} dy={START_DY_BY_ANCHOR[verticalAnchor]}>
                        {line}
                    </tspan>
                </text>
            ) : null}
        </svg>
    );
}
